import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/Icon";
import { Spinner } from "@/components/Spinner";
import { CallerAvatar } from "@/components/inboundCall/CallerAvatar";
import { useCallStackSlot } from "@/components/inboundCall/callStackSlot";
import { miniAction, miniStatus, windowBack } from "@/components/outgoingCall/outgoingCall";
import { useEscClose } from "@/lib/escClose";
import { fmtPhone } from "@/lib/phoneFormat";
import { CANDIDATE_PARAM, useOpenCandidate } from "@/pages/Candidates/candidateLink";
import { dialBlocked, hasActiveCall, subscribeActiveCall } from "./softphone";
import { useAudioDevices } from "./useAudioDevices";
import "./call.css";
// Свёрнутая плашка, кнопка «Свернуть» и ссылка на карточку — те же, что у
// окна входящего (.icall-*).
import "@/components/inboundCall/inboundCall.css";
import { afterOverlays, useBackClose } from "@/mobile/backClose";
import { CALL_INTERRUPTED_TEXT, useCallInterrupted } from "./callMode";
import { HoldButton, HoldHint } from "./HoldControls";
import { inCallLabel } from "./callHold";

export function fmtDur(sec: number): string {
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
}

export type CallStatus =
  | { state: "idle" }
  | { state: "connecting" }
  | { state: "ringing" }
  // Входящий: линия прослушки поднята, микрофон заглушён — рекрутёр слышит
  // звонящего и автоответчик, его самого не слышит никто. `since` — начало
  // прослушки; при перехвате отсчёт разговора начинается заново (in_call).
  | { state: "listening"; since: number }
  | { state: "in_call"; since: number }
  // Длительность несёт само состояние: в «завершён» её больше неоткуда взять,
  // а рекрутёру надо увидеть, сколько говорили, прежде чем окно закроется.
  | { state: "ended"; duration_s: number }
  | { state: "error"; message: string };

// Pre-call modal: pick the number (if several), microphone and audio output,
// then Позвонить (real call to the candidate via the GSM gateway) or Тест
// (dial the test line through the gateway to verify the setup). Device choices
// are handed to the softphone; audio output uses HTMLMediaElement.setSinkId.
//
// Окно одно на шелл, его держит OutgoingCallHost (софтфон — тоже его). Пока
// идёт звонок, окно сворачивается в плашку в стопке звонков — как окно
// входящего: под ней можно открыть карточку кандидата, сменить чат или
// раздел. Компонент при этом остаётся смонтированным: выбранный номер и
// устройства не теряются, а микрофон не запрашивается заново посреди звонка.
export function CallModal({
  personName,
  phones,
  candidateId = null,
  status,
  localStream,
  remoteStream,
  onDial,
  onHangup,
  onClose,
  minimized,
  onMinimize,
  notice = null,
  audioBlocked = false,
  onResumeAudio,
  micMuted = false,
  onToggleMic,
  holdSince = null,
  onToggleHold,
}: {
  personName: string;
  phones: string[];
  /** Кандидатура собеседника — на время звонка в окне есть ссылка на карточку. */
  candidateId?: string | null;
  status: CallStatus;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  onDial: (opts: { number: string; test: boolean; micId: string; speakerId: string }) => void;
  onHangup: () => void;
  onClose: () => void;
  minimized: boolean;
  /** Свернуть можно только идущий звонок (см. outgoingCall.ts). */
  onMinimize: (minimized: boolean) => void;
  /** «Сначала завершите текущий звонок: …» — позвонить пытались другому. */
  notice?: string | null;
  /** Браузер не дал играть звук собеседника — нужна кнопка (iOS). */
  audioBlocked?: boolean;
  onResumeAudio?: () => void;
  /** Микрофон выключен кнопкой «Без звука»; сама кнопка — только в разговоре. */
  micMuted?: boolean;
  onToggleMic?: () => void;
  /** Линия на удержании с этого момента; null — не на удержании. Таймер
   *  разговора при этом идёт — он считается от начала звонка. */
  holdSince?: number | null;
  onToggleHold?: () => void;
}) {
  const held = holdSince !== null;
  const active =
    status.state === "connecting" ||
    status.state === "ringing" ||
    status.state === "listening" ||
    status.state === "in_call";
  // Телефон: «Назад» убирает окно, а не переписку под ним. Идёт звонок — окно
  // сворачивается в плашку, и звонок продолжается; нет — закрывается. У
  // свёрнутого окна записи в истории нет: «Назад» там листает страницы.
  const back = () => (windowBack(status.state) === "minimize" ? onMinimize(true) : onClose());
  useBackClose(back, !minimized);
  // Esc — то же с клавиатуры, и клавиша остаётся в окне (lib/escClose). Иначе
  // её получала карточка кандидата под окном: закрывалась сама, а окно
  // оставалось висеть над списком; посреди разговора — рекрутёр сворачивал
  // окно и не находил карточку. Свёрнутая плашка Esc не занимает.
  useEscClose(back, !minimized);
  // Закрытие держим в ref: таймер ниже не должен зависеть от того, что придёт
  // в `onClose`. Когда окно открывали лента и карточка, там была инлайновая
  // стрелка, новая на каждой перерисовке (а лента перерисовывается от SSE
  // постоянно), — таймер в зависимостях сбрасывался бы бесконечно и не
  // срабатывал никогда.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Разговор кончился — окно закрывается само. Раньше оно висело, пока
  // рекрутёр не нажмёт кнопку, хотя собеседник уже положил трубку. Пара секунд
  // задержки — чтобы успеть прочитать, сколько говорили. Свёрнутая плашка
  // уходит так же: компонент один, таймер тот же.
  useEffect(() => {
    if (status.state !== "ended") return;
    const t = window.setTimeout(() => closeRef.current(), 2500);
    return () => window.clearTimeout(t);
  }, [status.state]);

  const [number, setNumber] = useState(phones[0] ?? "");
  // Список устройств и запомненный выбор — общий хук (им же пользуется окно
  // входящего звонка).
  // На телефоне списков нет (systemOnly): микрофон и выход — системные.
  const {
    mics,
    speakers,
    micId,
    setMicId,
    speakerId,
    setSpeakerId,
    permError,
    supportsSinkId,
    systemOnly,
  } = useAudioDevices();
  const interrupted = useCallInterrupted();
  const openCandidate = useOpenCandidate();
  // Куда встаёт свёрнутая плашка — место в стопке звонков InboundCallHost.
  const slot = useCallStackSlot();

  const dial = (test: boolean) => onDial({ number, test, micId, speakerId });
  // Во вкладке уже идёт другой звонок (перехваченный входящий, свёрнутый ради
  // карточки): второй сеанс того же SIP-пользователя 101 сорвал бы первому
  // регистрацию, а микрофон ушёл бы в обе линии.
  const anyCall = useSyncExternalStore(subscribeActiveCall, hasActiveCall);
  const blocked = dialBlocked(status.state, anyCall);

  const openCard = () => {
    if (!candidateId) return;
    // Звонили из самой карточки или из её чата (callCardId) — она уже открыта
    // под окном, второй раз открывать нечего: достаточно свернуться. Иначе
    // карточка открывается новой записью истории — на телефоне сперва
    // снимается запись окна (afterOverlays), иначе «Назад» из карточки вёл бы
    // в пустой шаг.
    const shown = new URLSearchParams(window.location.search).get(CANDIDATE_PARAM);
    if (shown !== candidateId) afterOverlays(() => openCandidate(candidateId));
    onMinimize(true);
  };

  const resumeBtn = (label: string) =>
    audioBlocked &&
    active &&
    onResumeAudio && (
      <button type="button" className="call-btn call-btn--call call-unmute" onClick={onResumeAudio}>
        {label}
      </button>
    );

  // «Без звука»: только в живом разговоре. До ответа и в прослушке микрофон
  // выключен сам, переключать там нечего.
  // На удержании микрофон выключен самим удержанием — переключать нечего.
  const muteBtn =
    status.state === "in_call" && onToggleMic && !held ? (
      <button
        type="button"
        className={"call-btn call-btn--test call-mute" + (micMuted ? " call-mute--on" : "")}
        onClick={onToggleMic}
        aria-pressed={micMuted}
        title={micMuted ? "Собеседник вас не слышит" : "Выключить микрофон"}
      >
        {micMuted ? "Включить микрофон" : "Без звука"}
      </button>
    ) : null;
  // «Удержание» — только в живом разговоре, как и «Без звука».
  const holdBtn =
    status.state === "in_call" && onToggleHold ? <HoldButton held={held} onToggle={onToggleHold} /> : null;

  if (minimized) {
    const action = miniAction(status.state);
    const waiting = status.state === "connecting" || status.state === "ringing";
    const bar = (
      <section className="icall-mini icall-mini--out" aria-label={`Звонок: ${personName}`}>
        <div className="icall-mini-row">
          <CallerAvatar
            call={{ person: { id: "", name: personName, avatar_resume_id: null, candidate_id: candidateId } }}
            size={36}
            ringing={waiting}
          />
          <div className="icall-mini-text">
            <div className="icall-mini-title" title={personName}>
              {personName}
            </div>
            <CallMiniStatus status={status} micMuted={micMuted} held={held} />
          </div>
          <button
            type="button"
            className="icall-icon-btn"
            onClick={() => onMinimize(false)}
            aria-label="Развернуть"
            title="Развернуть"
          >
            <Icon name="expand" size={15} />
          </button>
        </div>
        {/* Предупреждения окна — и на плашке: свёрнутым звонок может быть весь
            разговор, а без звука собеседника он бессмыслен. */}
        {interrupted && active && <div className="call-warn">{CALL_INTERRUPTED_TEXT}</div>}
        {resumeBtn("Включить звук")}
        {held && status.state === "in_call" && <HoldHint since={holdSince} />}
        {action === "hang" && (
          <div className={"icall-mini-actions" + (holdBtn ? " call-actions--stack" : "")}>
            {holdBtn}
            {muteBtn}
            <button type="button" className="call-btn call-btn--hang" onClick={onHangup}>
              Завершить
            </button>
          </div>
        )}
        {/* Звонок не удался, пока окно было свёрнуто: плашка сама не уходит —
            ошибку надо увидеть, а из окна можно набрать снова. */}
        {action === "dismiss" && (
          <div className="icall-mini-actions">
            <button type="button" className="call-btn call-btn--test" onClick={onClose}>
              Закрыть
            </button>
            <button
              type="button"
              className="call-btn call-btn--test icall-close-btn--wide"
              onClick={() => onMinimize(false)}
            >
              Развернуть
            </button>
          </div>
        )}
      </section>
    );
    // Стопки нет (шелл без входящих) — плашка встаёт на то же место сама.
    return slot ? createPortal(bar, slot) : <div className="icall-stack">{bar}</div>;
  }

  return (
    <div className="modal-backdrop ocall-backdrop" onClick={active ? undefined : onClose}>
      <div
        className="modal call-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Звонок: ${personName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="call-head">
          <div>
            <h2 className="call-title">Звонок</h2>
            <div className="call-sub">{personName}</div>
          </div>
          {/* До набора окно закрывают; идущий звонок — сворачивают («Завершить» внизу). */}
          {active ? (
            <button
              type="button"
              className="icall-icon-btn"
              onClick={() => onMinimize(true)}
              aria-label="Свернуть"
              title="Свернуть"
            >
              <Icon name="minimize" size={16} />
            </button>
          ) : (
            <button type="button" className="doc-modal-x" onClick={onClose} aria-label="Закрыть">
              ×
            </button>
          )}
        </header>

        <div className="call-body">
          {active && candidateId && (
            <button type="button" className="icall-card-link" onClick={openCard}>
              <Icon name="user" size={14} strokeWidth={1.8} />
              Карточка кандидата
              <Icon name="chevron-right" size={14} strokeWidth={1.8} />
            </button>
          )}

          {phones.length > 1 ? (
            <label className="call-field">
              <span className="call-label">Номер</span>
              <select className="call-input" value={number} onChange={(e) => setNumber(e.target.value)} disabled={active}>
                {phones.map((p) => (
                  <option key={p} value={p}>
                    {fmtPhone(p)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="call-field">
              <span className="call-label">Номер</span>
              <div className="call-number">{fmtPhone(number)}</div>
            </div>
          )}

          {mics === null && <Spinner center label="Устройства…" />}
          {permError && <div className="call-warn">{permError}</div>}
          {mics !== null && !permError && !systemOnly && (
            <>
              <label className="call-field">
                <span className="call-label">Микрофон</span>
                <select className="call-input" value={micId} onChange={(e) => setMicId(e.target.value)} disabled={active}>
                  {mics.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="call-field">
                <span className="call-label">Аудиовыход</span>
                <select
                  className="call-input"
                  value={speakerId}
                  onChange={(e) => setSpeakerId(e.target.value)}
                  disabled={active || !supportsSinkId}
                >
                  <option value="">По умолчанию</option>
                  {speakers.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {!supportsSinkId && (
                  <span className="call-hint">Браузер не даёт выбирать аудиовыход — системный по умолчанию.</span>
                )}
              </label>
            </>
          )}

          <CallStatusLine status={status} held={held} muted={micMuted} />
          {notice && <div className="call-warn">{notice}</div>}
          {blocked && <div className="call-warn">Вы уже на звонке — сначала завершите его.</div>}
          {interrupted && active && <div className="call-warn">{CALL_INTERRUPTED_TEXT}</div>}
          {resumeBtn("Включить звук собеседника")}
          {held && status.state === "in_call" && <HoldHint since={holdSince} />}
          {micMuted && !held && status.state === "in_call" && (
            <div className="icall-hint call-mute-hint" role="status">
              <Icon name="mic" size={13} strokeWidth={1.8} />
              Микрофон выключен: собеседник вас не слышит.
            </div>
          )}

          {status.state === "in_call" && (
            <div className="call-waves">
              <CallWaves local={localStream} remote={remoteStream} />
            </div>
          )}
        </div>

        <footer className={"call-actions" + (active && holdBtn ? " call-actions--stack" : "")}>
          {active ? (
            <>
              {holdBtn}
              {muteBtn}
              <button type="button" className="call-btn call-btn--hang" onClick={onHangup}>
                Завершить
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="call-btn call-btn--test"
                onClick={() => dial(true)}
                disabled={mics === null || blocked}
              >
                Тест
              </button>
              <button
                type="button"
                className="call-btn call-btn--call"
                onClick={() => dial(false)}
                disabled={mics === null || !number || blocked}
              >
                Позвонить
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

export const WAVE_ME = "#0260e8";
export const WAVE_THEM = "#008736";

const WAVE_BARS = 60; // × WAVE_STEP ms ≈ 5s window
const WAVE_STEP = 85; // ms per bar — the timeline «flows» left as bars are added

// Voice-message-style scrolling waveform: a flowing timeline of the last ~5s of
// loudness. Each WAVE_STEP a new amplitude sample is pushed on the right and the
// oldest drops off the left. Recruiter (blue) and candidate (green) bars are
// overlaid on one canvas — mostly one speaks at a time, так их видно раздельно.
export function CallWaves({ local, remote }: { local: MediaStream | null; remote: MediaStream | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const actx = new AC();

    const makeAnalyser = (stream: MediaStream | null) => {
      if (!stream || stream.getAudioTracks().length === 0) return null;
      const src = actx.createMediaStreamSource(stream);
      const an = actx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      return { an, buf: new Uint8Array(new ArrayBuffer(an.fftSize)), src };
    };
    const a = makeAnalyser(local);
    const b = makeAnalyser(remote);

    // RMS loudness (0..1) of the current audio frame.
    const rms = (node: { an: AnalyserNode; buf: Uint8Array } | null) => {
      if (!node) return 0;
      node.an.getByteTimeDomainData(node.buf as Uint8Array<ArrayBuffer>);
      let s = 0;
      for (let i = 0; i < node.buf.length; i++) {
        const x = (node.buf[i] - 128) / 128;
        s += x * x;
      }
      return Math.sqrt(s / node.buf.length);
    };

    const histA = new Array(WAVE_BARS).fill(0);
    const histB = new Array(WAVE_BARS).fill(0);
    let last = 0;
    let raf = 0;

    // A center line; blue (me) bars grow UP from it, green (candidate) grow DOWN.
    const bars = (
      c: CanvasRenderingContext2D,
      hist: number[],
      color: string,
      w: number,
      h: number,
      dir: "up" | "down",
    ) => {
      const bw = w / WAVE_BARS;
      const mid = h / 2;
      const half = h / 2 - 1;
      c.fillStyle = color;
      for (let i = 0; i < WAVE_BARS; i++) {
        const v = Math.min(1, hist[i] * 5); // scale quiet speech up
        const bh = Math.max(3, v * half);
        const x = i * bw;
        const y = dir === "up" ? mid - bh : mid;
        const r = Math.min(bw * 0.3, bh / 2);
        c.beginPath();
        c.roundRect(x + bw * 0.2, y, bw * 0.6, bh, r);
        c.fill();
      }
    };

    const draw = (t: number) => {
      raf = requestAnimationFrame(draw);
      if (t - last >= WAVE_STEP) {
        last = t;
        histA.push(rms(a));
        histA.shift();
        histB.push(rms(b));
        histB.shift();
      }
      const cv = canvasRef.current;
      if (!cv) return;
      const c = cv.getContext("2d");
      if (!c) return;
      const w = cv.width;
      const h = cv.height;
      c.clearRect(0, 0, w, h);
      // faint center line
      c.strokeStyle = "#e6e6e6";
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(0, h / 2);
      c.lineTo(w, h / 2);
      c.stroke();
      bars(c, histA, WAVE_ME, w, h, "up"); // recruiter — up from center
      bars(c, histB, WAVE_THEM, w, h, "down"); // candidate — down from center
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      a?.src.disconnect();
      b?.src.disconnect();
      actx.close().catch(() => {});
    };
  }, [local, remote]);

  return <canvas ref={canvasRef} width={356} height={56} className="call-wave-canvas" />;
}

/** Строка состояния свёрнутой плашки: «Вызов…», «Разговор · 03:25», итог. */
function CallMiniStatus({
  status,
  micMuted = false,
  held = false,
}: {
  status: CallStatus;
  micMuted?: boolean;
  held?: boolean;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (status.state !== "in_call") return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [status.state]);
  // Свёрнутым звонок может быть весь разговор — забытые «Без звука» и
  // «Удержание» должны быть видны и на плашке.
  const { text, tone } = miniStatus(status, Date.now(), { held, muted: micMuted });
  return (
    <div className={"icall-mini-status icall-mini-status--" + tone} title={text}>
      <span>{text}</span>
    </div>
  );
}

export function CallStatusLine({
  status,
  held = false,
  muted = false,
}: {
  status: CallStatus;
  held?: boolean;
  muted?: boolean;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (status.state !== "in_call" && status.state !== "listening") return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [status.state]);

  if (status.state === "idle") return null;
  let text = "";
  let cls = "";
  if (status.state === "connecting") (text = "Соединение…"), (cls = "call-status--wait");
  else if (status.state === "ringing") (text = "Вызов…"), (cls = "call-status--wait");
  else if (status.state === "listening") {
    const sec = Math.max(0, Math.floor((Date.now() - status.since) / 1000));
    text = `Слушаете автоответчик · ${fmtDur(sec)}`;
    cls = "call-status--wait";
  } else if (status.state === "in_call") {
    const sec = Math.floor((Date.now() - status.since) / 1000);
    // Таймер идёт и на удержании: рекрутёру важно, сколько всего длится звонок.
    text = inCallLabel(fmtDur(Math.max(0, sec)), { held, muted });
    cls = held ? "call-status--wait" : "call-status--live";
  } else if (status.state === "ended") {
    text = status.duration_s
      ? `Звонок завершён · ${fmtDur(status.duration_s)}`
      : "Звонок завершён";
    cls = "call-status--wait";
  }
  else if (status.state === "error") (text = status.message), (cls = "call-status--err");

  return <div className={"call-status " + cls}>{text}</div>;
}
